/**
 * The bottom pill that confirms every mutation. Prototype: docs/HRMS.jsx line 850-859.
 *
 * The prototype had one neutral pill that vanished after ~2.5 s. Production keeps the
 * look and adds the three things a confirmation surface needs once real failures run
 * through it:
 *
 *  · **A tone.** Six screens push error text through this component; a failure that is
 *    pixel-identical to a success is not a message, it is decoration. `error` is red
 *    and carries `role="alert"` + `aria-live="assertive"` so a screen reader interrupts
 *    for it, exactly as it should for something the user must act on. A toast offering
 *    Undo interrupts for the same reason — the offer expires. Plain confirmations stay
 *    polite `role="status"`.
 *  · **A dismiss.** Every toast can be closed by hand, and hovering or focusing one
 *    holds it open — an error a manager needs to read outlives the 2.8 s that suits a
 *    confirmation.
 *  · **Longer lives where the user has a decision.** The behaviour doc's 2.2–2.8 s
 *    window (line 33) describes a confirmation; an Undo is a reversible destructive
 *    action and gets the Gmail-style window instead. See the DURATION constants.
 */
import { AlertTriangle, CheckCircle2, Info, RotateCcw, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import "./ui.css";

export type ToastTone = "success" | "error" | "info";

/** A plain confirmation — the prototype's window, unchanged. */
export const TOAST_DURATION_MS = 2800;
/** A failure: long enough to read the sentence and decide what to do about it. */
export const TOAST_ERROR_DURATION_MS = 8000;
/** An Undo window. Reversing a destructive action in 2.5 s is not an offer. */
export const TOAST_UNDO_DURATION_MS = 10_000;

const TONE_ICON = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
} as const;

/** The default life of a toast, given its tone and whether it offers an action. */
export function toastDuration(tone: ToastTone, hasAction: boolean): number {
  if (hasAction) return TOAST_UNDO_DURATION_MS;
  return tone === "error" ? TOAST_ERROR_DURATION_MS : TOAST_DURATION_MS;
}

export interface ToastProps {
  text: ReactNode;
  tone?: ToastTone;
  /** Label for the trailing action button, normally "Undo". */
  action?: string;
  onAction?: () => void;
  /** Called after `duration`, on dismiss, and on Escape; omit to make it permanent. */
  onDismiss?: () => void;
  duration?: number;
}

export function Toast({ text, tone = "success", action, onAction, onDismiss, duration }: ToastProps) {
  // Hovering or focusing the pill parks the timer. Set while the pointer is over it or
  // the Undo/close button holds focus, so a toast can never expire mid-reach.
  const [held, setHeld] = useState(false);
  const life = duration ?? toastDuration(tone, Boolean(action));
  const Icon = TONE_ICON[tone];

  useEffect(() => {
    if (!onDismiss || held) return;
    const timer = window.setTimeout(onDismiss, life);
    return () => window.clearTimeout(timer);
  }, [onDismiss, life, held, text]);

  // Interrupt for the two cases where the user has something to do: a failure, and an
  // Undo that expires. A plain confirmation stays polite.
  const urgent = tone === "error" || Boolean(action);

  return (
    <div
      className={`ui-toast ui-toast--${tone}`}
      role={urgent ? "alert" : "status"}
      aria-live={urgent ? "assertive" : "polite"}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <Icon size={14} className="ui-toast__icon" aria-hidden="true" />
      <span className="ui-toast__text">{text}</span>
      {action ? (
        <button type="button" className="ui-toast__action" onClick={onAction}>
          <RotateCcw size={12} aria-hidden="true" /> {action}
        </button>
      ) : null}
      {onDismiss ? (
        <button type="button" className="ui-toast__close" onClick={onDismiss}>
          <X size={13} aria-hidden="true" />
          <span className="bp-sr-only">Dismiss</span>
        </button>
      ) : null}
    </div>
  );
}

export default Toast;
